import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBenchmark } from "../lib/api";
import MaslowSphere from "./MaslowSphere";
import { Columns2, Layers, Users } from "lucide-react";

/** One subject against the averaged sphere of every other qualifying engagement. */
export const Benchmark = ({ subject }) => {
  const [minTurns, setMinTurns] = useState(8);
  const [mode, setMode] = useState("merged");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setErr("");
    fetchBenchmark(subject, minTurns)
      .then(setD)
      .catch((e) => {
        setD(null);
        setErr(e?.response?.data?.detail || "could not build the benchmark");
      });
  }, [subject, minTurns]);

  useEffect(() => {
    load();
  }, [load]);

  const bm = d?.benchmark;

  // merged sphere: subject-only / cohort-only / shared, coded like the compare view
  const merged = useMemo(() => {
    if (!bm) return null;
    return {
      tiers: d.subject_map.tiers,
      nodes: bm.rows.map((r) => {
        const sal = Math.max(r.subject_salience, r.pool_mean);
        const side =
          r.subject_salience > 0 && r.pool_mean > 0
            ? "shared"
            : r.subject_salience > 0
              ? "a"
              : r.pool_mean > 0
                ? "b"
                : "none";
        return {
          id: r.id,
          name: r.name,
          tier: r.tier,
          side,
          salience: sal,
          radius: 0.06 + 0.1 * (sal / 100),
          position: (d.subject_map.nodes.find((n) => n.id === r.id) || {}).position,
          tier_name: "",
          business_lens: r.business_lens,
          fill_confidence: sal,
          evidence: [],
        };
      }),
      influence_nodes: (bm.pool_map.influence_nodes || []).map((e) => ({ ...e, side: "b" })),
      candidate_edges: [],
    };
  }, [bm, d]);

  const compareSides = useMemo(
    () => (merged ? Object.fromEntries(merged.nodes.map((n) => [n.id, n.side])) : null),
    [merged]
  );

  return (
    <div data-testid="admin-benchmark" className="space-y-7">
      <div className="flex flex-wrap items-center gap-4">
        <div>
          <p className="label">Cohort benchmark</p>
          <p className="mt-2 max-w-xl text-xs leading-relaxed text-zinc-500">
            Every other engagement with enough interview behind it, averaged into one sphere, with
            this subject scored in sigma against it.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="label" htmlFor="min-turns">
            Pool floor
          </label>
          <input
            id="min-turns"
            data-testid="benchmark-min-turns"
            type="number"
            min={1}
            max={200}
            value={minTurns}
            onChange={(e) => setMinTurns(Math.max(1, Number(e.target.value) || 1))}
            className="w-20 border border-[var(--line-hi)] bg-[#0a0a0d] px-3 py-2 font-data text-[11px] text-zinc-100 outline-none"
          />
          <span className="font-data text-[10px] text-zinc-600">scored turns</span>
          <div className="flex items-center gap-1">
            {[
              ["merged", "Merged", Layers],
              ["side", "Side by side", Columns2],
            ].map(([key, text, Icon]) => (
              <button
                key={key}
                data-testid={`benchmark-view-${key}`}
                onClick={() => setMode(key)}
                className={`flex items-center gap-2 px-3 py-2 font-data text-[10px] uppercase tracking-[0.14em] transition-colors ${
                  mode === key ? "bg-zinc-100 text-[var(--void)]" : "text-zinc-500 hover:text-zinc-200"
                }`}
              >
                <Icon size={11} strokeWidth={1.8} />
                {text}
              </button>
            ))}
          </div>
        </div>
      </div>

      {err && <p className="font-data text-[11px] text-zinc-400">— {err}</p>}

      <div className="flex flex-wrap items-center gap-3 border border-[var(--line)] px-4 py-3">
        <Users size={12} strokeWidth={1.7} className="text-zinc-500" />
        <span data-testid="benchmark-pool" className="font-data text-[11px] text-zinc-300">
          pool of {d?.pool_members?.length ?? 0}
          {d?.pool_members?.length
            ? ` — ${d.pool_members.map((m) => `${m.name} (${m.turns})`).join(", ")}`
            : ""}
        </span>
        {d?.excluded?.length > 0 && (
          <span className="font-data text-[10px] text-zinc-700">
            excluded below the floor: {d.excluded.map((m) => `${m.name} (${m.turns})`).join(", ")}
          </span>
        )}
      </div>

      {d?.thin_pool && (
        <p data-testid="benchmark-thin-warning" className="font-data text-[11px] text-zinc-300">
          — Thin pool. The sigma arithmetic is sound but the cohort is only{" "}
          {bm.summary.pool_size} engagement{bm.summary.pool_size === 1 ? "" : "s"} deep, so read the
          deviations as directional until more people have sat the interview.
        </p>
      )}

      {!bm && !err && (
        <p className="font-data text-[11px] text-zinc-600">
          No other engagement clears {minTurns} scored turns yet, so there is nothing to average.
          Lower the floor or interview more subjects.
        </p>
      )}

      {bm && (
        <>
          <div className="grid grid-cols-2 gap-px bg-[var(--line)] sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Pool size", bm.summary.pool_size, "engagements averaged"],
              ["Measured", bm.summary.measured_categories, "categories comparable"],
              ["Mean |σ|", bm.summary.mean_abs_z, "typical deviation"],
              ["Above cohort", bm.summary.above, "≥ 0.6σ"],
              ["Below cohort", bm.summary.below, "≤ −0.6σ"],
              ["Typical", bm.summary.typical, "within 0.6σ"],
            ].map(([k, v, s]) => (
              <div key={k} className="border border-[var(--line)] px-4 py-3">
                <p className="label">{k}</p>
                <p className="mt-1.5 font-data text-lg tabular-nums leading-none text-zinc-100">
                  {v}
                </p>
                <p className="mt-1.5 truncate font-data text-[10px] text-zinc-600">{s}</p>
              </div>
            ))}
          </div>

          {mode === "merged" ? (
            <div
              className="relative h-[28rem] border border-[var(--line)]"
              data-testid="benchmark-merged"
            >
              <MaslowSphere map={merged} compare={compareSides} showCandidates={false} />
              <div className="panel pointer-events-none absolute left-4 top-4 px-4 py-3">
                <p className="label">Subject over cohort</p>
                <ul className="mt-2 space-y-1 font-data text-[10px] text-zinc-500">
                  <li>solid bright — carried by both</li>
                  <li>solid dim — this subject only</li>
                  <li>wireframe — cohort only, unmapped here</li>
                  <li>dashed edge — cohort influence line</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2" data-testid="benchmark-side-by-side">
              <div className="relative h-80 border border-[var(--line)]">
                <MaslowSphere
                  map={d.subject_map}
                  heat={d.subject_heat || {}}
                  showCandidates={false}
                />
                <div className="panel pointer-events-none absolute left-4 top-4 px-3 py-2">
                  <p className="label">This subject</p>
                  <p className="mt-1 font-data text-[10px] text-zinc-600">
                    C {d.subject_metrics.predictive_confidence}% ·{" "}
                    {d.subject_map.coverage.filled}/{d.subject_map.coverage.total ?? 46}
                  </p>
                </div>
              </div>
              <div className="relative h-80 border border-[var(--line)]">
                <MaslowSphere map={bm.pool_map} showCandidates={false} />
                <div className="panel pointer-events-none absolute left-4 top-4 px-3 py-2">
                  <p className="label">Cohort average</p>
                  <p className="mt-1 font-data text-[10px] text-zinc-600">
                    {bm.summary.pool_size} engagements · {bm.pool_map.coverage.filled}/
                    {bm.pool_map.coverage.total ?? 46}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <p className="label">Where they diverge most</p>
              <ul className="mt-4" data-testid="benchmark-divergent">
                {bm.divergent.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`benchmark-row-${r.id}`}
                    className="grid grid-cols-[1fr_auto] items-baseline gap-4 border-b border-[var(--line)] py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs text-zinc-200">
                        T{r.tier} {r.name}
                      </p>
                      <p className="mt-1 font-data text-[10px] text-zinc-600">
                        them {r.subject_salience} vs cohort {r.pool_mean} (±{r.pool_sd}, n
                        {r.pool_n}) · {r.verdict}
                      </p>
                    </div>
                    <span className="font-data text-[11px] tabular-nums text-zinc-300">
                      {r.z > 0 ? "+" : ""}
                      {r.z}σ
                    </span>
                  </li>
                ))}
                {bm.divergent.length === 0 && (
                  <p className="font-data text-[11px] text-zinc-600">
                    Nothing measurable against the pool yet.
                  </p>
                )}
              </ul>
            </div>

            <div>
              <p className="label">Most common influence lines in the cohort</p>
              <ul className="mt-4" data-testid="benchmark-pool-edges">
                {(bm.pool_map.influence_nodes || []).slice(0, 10).map((e) => (
                  <li key={e.id} className="border-b border-[var(--line)] py-3">
                    <p className="font-data text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                      {Math.round(e.prevalence * 100)}% of the cohort · T{e.high_tier}→T{e.low_tier}{" "}
                      · {e.valence}
                    </p>
                    <p className="mt-1 text-xs text-zinc-200">
                      {e.high_name} ⟶ {e.low_name}
                    </p>
                    <p className="mt-1 font-data text-[10px] text-zinc-600">
                      mean strength {e.strength} across {e.subjects} subject
                      {e.subjects === 1 ? "" : "s"}
                    </p>
                  </li>
                ))}
                {(bm.pool_map.influence_nodes || []).length === 0 && (
                  <p className="font-data text-[11px] text-zinc-600">
                    No influence candidates anywhere in the pool yet.
                  </p>
                )}
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Benchmark;
